import { useState, useMemo } from 'react';
import { Popover, Select, Loader } from '@mantine/core';
import { IconUserCog, IconChevronRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useBusinesses, useUpdateServiceBooking } from '@/hooks/api-hooks';
import { Business, ServiceBooking, ServiceOrder } from '@turizi/shared';
import { SERVICE_CATEGORIES } from '@turizi/shared';

interface Props {
    order: ServiceOrder;
    onCloseMenu?: () => void;
    onPopoverOpenChange?: (open: boolean) => void;
}

export default function SetOrderCoordinatorPopoverItem({
    order,
    onCloseMenu,
    onPopoverOpenChange,
}: Props): JSX.Element {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);
    const [saving, setSaving] = useState(false);
    const { mutateAsync: updateServiceBooking } = useUpdateServiceBooking();

    const handleOpenChange = (newOpened: boolean) => {
        setOpened(newOpened);
        onPopoverOpenChange?.(newOpened);
    };

    const {
        data: businessesResponse,
        isLoading: loadingBusinesses
    } = useBusinesses({
        category: SERVICE_CATEGORIES.coordinator
    });
    const coordinatorBusinesses = businessesResponse?.data || [];

    const bookings = useMemo(() => {
        const linkedBookings = order?.service_order_bookings
            ?.map(link => link?.service_booking)
            .filter(Boolean) as ServiceBooking[] || [];

        const map = new Map<number, ServiceBooking>();
        linkedBookings.forEach(b => {
            if (b?.id && !map.has(b.id)) map.set(b.id, b);
        });
        return Array.from(map.values());
    }, [order]);

    const currentCoordinatorId = useMemo(() => {
        if (!bookings.length) return null;
        const firstId = bookings[0]?.coordinator_id;
        const allSame = bookings.every(b => b.coordinator_id === firstId);
        return allSame && firstId ? firstId.toString() : (firstId ? firstId.toString() : null);
    }, [bookings]);

    const handleSelectCoordinator = async (val: string | null) => {
        if (!bookings.length) return;
        const newCoordinatorId = val ? Number(val) : null;
        setSaving(true);
        try {
            await Promise.all(
                bookings.map(b =>
                    updateServiceBooking({
                        id: b.id,
                        coordinator_id: newCoordinatorId,
                    })
                )
            );
            handleOpenChange(false);
            onCloseMenu?.();
        } catch (error) {
            console.error('Failed to update coordinator for bookings:', error);
        } finally {
            setSaving(false);
        }
    };

    const loading = saving || loadingBusinesses;

    return (
        <Popover
            opened={opened}
            onChange={handleOpenChange}
            position="right-start"
            withArrow
            shadow="md"
            withinPortal
        >
            <Popover.Target>
                <div
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleOpenChange(!opened);
                    }}
                    className="w-full flex items-center justify-between px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 cursor-pointer rounded-sm select-none transition-colors"
                >
                    <div className="flex items-center gap-2">
                        <IconUserCog size={16} className="text-indigo-600" />
                        <span>{t('coordinator')}</span>
                    </div>
                    <IconChevronRight size={14} className="text-gray-400" />
                </div>
            </Popover.Target>
            <Popover.Dropdown onClick={(e) => e.stopPropagation()} p="xs" className="w-[200px]">
                <Select
                    variant="filled"
                    searchable
                    clearable
                    size="xs"
                    placeholder={t('coordinator')}
                    value={currentCoordinatorId}
                    data={coordinatorBusinesses.map((b: Business) => ({
                        value: b.id.toString(),
                        label: b.name,
                    }))}
                    onChange={handleSelectCoordinator}
                    disabled={loading}
                    rightSection={loading ? <Loader size="xs" color="black" /> : null}
                    comboboxProps={{ keepMounted: false, withinPortal: false }}
                    allowDeselect
                />
            </Popover.Dropdown>
        </Popover>
    );
}
